import React, { useState } from 'react';
import api from '../api';

const WORK_DAYS_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function SetupWizard({ user, onComplete }) {
  const [step, setStep] = useState(1); // 1=company, 2=hours, 3=invite
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [companyName, setCompanyName] = useState(user?.workspace?.name || '');
  const [workDays, setWorkDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const [clockIn, setClockIn] = useState('09:00');
  const [workHours, setWorkHours] = useState('8');
  const [invites, setInvites] = useState([{ name: '', email: '' }]);
  const [saving, setSaving] = useState(false);
  const [skipInvite, setSkipInvite] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const toggleDay = (day) => {
    setWorkDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleFinish = async () => {
    setSaving(true);
    setError('');
    try {
      // Save company settings
      await api.put('/settings', {
        company_name: companyName,
        work_days: JSON.stringify(workDays),
        expected_clock_in: clockIn,
        expected_work_hours: workHours,
      });

      // Upload logo if selected
      if (logo) {
        const fd = new FormData();
        fd.append('logo', logo);
        await api.post('/settings/logo', fd);
      }

      // Invite employees
      if (!skipInvite) {
        for (const inv of invites) {
          if (inv.name && inv.email) {
            try {
              await api.post('/employees', { name: inv.name, email: inv.email, role: 'employee' });
            } catch (e) { /* skip failed invites */ }
          }
        }
      }

      // Mark setup complete
      await api.put('/workspace/setup-complete');
      setDone(true);
      setTimeout(() => {
        onComplete();
        // Fallback: force reload if onComplete doesn't navigate
        setTimeout(() => window.location.reload(), 500);
      }, 1200);
    } catch (e) {
      console.error('Setup error:', e);
      setError(e?.response?.data?.error || e.message || 'Setup failed. Please try again.');
    }
    setSaving(false);
  };

  const TOTAL_STEPS = 3;

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '60px 36px', width: 420, boxShadow: '0 20px 60px rgba(0,0,0,.3)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🎉</div>
          <h2 style={{ color: '#1e1b4b' }}>You're all set!</h2>
          <p style={{ color: '#64748b' }}>Redirecting to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', width: 480, boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e1b4b' }}>HR<span style={{ color: '#4f46e5' }}>Track</span></div>
          <div style={{ color: '#64748b', marginTop: 6, fontSize: '0.9rem' }}>Set up your workspace</div>
          {/* Progress bar */}
          <div style={{ marginTop: 16, background: '#e2e8f0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
            <div style={{ width: `${(step / TOTAL_STEPS) * 100}%`, height: '100%', background: '#4f46e5', transition: 'width .3s' }} />
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 6 }}>Step {step} of {TOTAL_STEPS}</div>
        </div>

        {/* Step 1: Company info & logo */}
        {step === 1 && (
          <>
            <h3 style={{ color: '#1e1b4b', marginBottom: 20 }}>Company information</h3>
            <div className="form-group">
              <label>Company Name</label>
              <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Corp" />
            </div>
            <div className="form-group">
              <label>Company Logo <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {logoPreview && <img src={logoPreview} alt="logo" style={{ height: 56, width: 56, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }} />}
                <label style={{ cursor: 'pointer', padding: '8px 16px', background: '#f1f5f9', borderRadius: 8, fontSize: '0.875rem', color: '#475569', border: '1px solid #e2e8f0' }}>
                  {logo ? 'Change logo' : 'Upload logo'}
                  <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Work schedule */}
        {step === 2 && (
          <>
            <h3 style={{ color: '#1e1b4b', marginBottom: 20 }}>Work schedule</h3>
            <div className="form-group">
              <label>Work Days</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {WORK_DAYS_OPTIONS.map(day => (
                  <button key={day} type="button" onClick={() => toggleDay(day)}
                    style={{ padding: '6px 12px', borderRadius: 20, border: '2px solid', borderColor: workDays.includes(day) ? '#4f46e5' : '#e2e8f0', background: workDays.includes(day) ? '#eef2ff' : '#fff', color: workDays.includes(day) ? '#4f46e5' : '#64748b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Expected Clock-In</label>
                <input type="time" value={clockIn} onChange={e => setClockIn(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Daily Work Hours</label>
                <input type="number" min="1" max="24" value={workHours} onChange={e => setWorkHours(e.target.value)} />
              </div>
            </div>
          </>
        )}

        {/* Step 3: Invite first employee */}
        {step === 3 && (
          <>
            <h3 style={{ color: '#1e1b4b', marginBottom: 8 }}>Invite your first employee</h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 20 }}>You can always add more later from the Employees page.</p>

            {!skipInvite && invites.map((inv, i) => (
              <div key={i} style={{ background: '#f8fafc', borderRadius: 10, padding: '16px', marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Full Name</label>
                    <input value={inv.name} onChange={e => setInvites(a => a.map((x, j) => j===i ? { ...x, name: e.target.value } : x))} placeholder="Jane Smith" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Email</label>
                    <input type="email" value={inv.email} onChange={e => setInvites(a => a.map((x, j) => j===i ? { ...x, email: e.target.value } : x))} placeholder="jane@acme.com" />
                  </div>
                </div>
              </div>
            ))}

            {!skipInvite && (
              <>
                <button type="button" onClick={() => setInvites(a => [...a, { name: '', email: '' }])}
                  style={{ background: 'none', border: '2px dashed #e2e8f0', borderRadius: 8, padding: '8px 16px', color: '#64748b', cursor: 'pointer', width: '100%', marginBottom: 12, fontSize: '0.875rem' }}>
                  + Add another employee
                </button>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', color: '#166534', marginBottom: 8 }}>
                  📧 Each employee will receive an invite email to set their own password.
                </div>
              </>
            )}

            <button type="button" onClick={() => setSkipInvite(s => !s)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>
              {skipInvite ? 'Add employees now' : 'Skip for now'}
            </button>
          </>
        )}

        {error && (
          <div style={{ marginTop: 16, padding: '10px 14px', background: '#fef2f2', color: '#dc2626', borderRadius: 8, fontSize: '0.875rem' }}>
            {error}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="btn" style={{ flex: 1, justifyContent: 'center', padding: '10px', background: '#f1f5f9', color: '#475569' }}>
              Back
            </button>
          )}
          {step < TOTAL_STEPS ? (
            <button onClick={() => setStep(s => s + 1)} className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '10px' }}>
              Continue →
            </button>
          ) : (
            <button onClick={handleFinish} disabled={saving} className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '10px' }}>
              {saving ? 'Setting up…' : 'Launch my workspace 🚀'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
